import { Shield, Check, Phone, Star, MapPin } from "lucide-react";
import PestOnlyCalculator from "../components/PestOnlyCalculator";
import { Logo } from "../components/Layout";
import { Header } from "../components/Layout";
import IpswichBreadcrumb from "../components/IpswichBreadcrumb";

const suburbs = ["Ipswich CBD", "Brassall", "Booval", "Bundamba", "Goodna", "Redbank Plains", "Collingwood Park", "Bellbird Park", "Ripley", "Springfield", "North Ipswich", "East Ipswich"];

export default function PestControlIpswichPage() {
  return (
    <>
      <>
        <title>Pest Control Ipswich | Pest Free Ipswich & Brisbane</title>
        <meta name="description" content="Professional pest control across Ipswich. Cockroaches, ants, spiders, termites & rodents. Same day service, QBCC licensed. Get an instant quote." />
      </>

      <Header />
      <div className="min-h-screen bg-slate-50">
        <IpswichBreadcrumb current="Pest Control" />
        <section className="bg-gradient-to-br from-[#8B2500] via-[#6B1D00] to-[#E74C3C]/20 text-white pt-28 pb-16 md:pt-32 md:pb-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#E74C3C]/20 border border-[#E74C3C]/30 rounded-full px-4 py-1.5 mb-5">
                  <MapPin className="w-4 h-4 text-[#E74C3C]" />
                  <span className="text-sm font-medium text-[#E74C3C]">Ipswich & Surrounds</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight">
                  Pest Control <span className="text-[#7ABC32]">Ipswich</span>
                </h1>
                <p className="text-lg text-slate-300 mb-6">
                  Ipswich's local pest control experts. From cockroaches and ants to termites and rodents — we handle it all with professional-grade treatments and fast turnaround.
                </p>
                <div className="flex flex-wrap gap-3 mb-6">
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />Same Day Service</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />QBCC Licensed</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />Family Safe</div>
                </div>
                <div className="flex items-center gap-3">
                  <a href="tel:0737081422" className="flex items-center gap-2 bg-[#7ABC32] hover:bg-[#6AAD28] text-white font-semibold px-6 py-3 rounded-full transition-all">
                    <Phone className="w-5 h-5" /> (07) 3708 1422
                  </a>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
                    <span className="text-sm text-slate-300 ml-1">4.9 on Google</span>
                  </div>
                </div>
              </div>
              <div className="flex justify-center">
                <Logo className="h-32 md:h-40 opacity-90" />
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16" id="calculator">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-800">Get Your Instant Price</h2>
              <p className="text-slate-600 mt-2">Pest control & termite inspection — instant quote</p>
            </div>
            <PestOnlyCalculator source="ipswich_pest" />
          </div>
        </section>

        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-8">Common Pest Issues in Ipswich</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { title: "Cockroaches & Ants", desc: "Ipswich's warm, humid climate makes it a hotspot for cockroaches and ants year-round. Both old and new homes are affected.", color: "#E74C3C" },
                { title: "Spiders", desc: "Redbacks, huntsmen and garden spiders thrive in Ipswich's bushland-adjacent suburbs. Regular treatment keeps them at bay.", color: "#E74C3C" },
                { title: "Termites", desc: "Ipswich is one of the highest termite-risk areas in Queensland. Older timber homes and newer builds on clay soil are equally vulnerable.", color: "#D2691E" },
                { title: "Rodents", desc: "Rats and mice are common in Ipswich, especially near creeks and older suburbs. Bait stations and entry point sealing are essential.", color: "#5BC0DE" },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-4 rounded-lg bg-slate-50">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: item.color + '15' }}>
                    <Shield className="w-5 h-5" style={{ color: item.color }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800">{item.title}</h3>
                    <p className="text-sm text-slate-600 mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">Serving All Ipswich Suburbs</h2>
            <div className="flex flex-wrap justify-center gap-2">
              {suburbs.map(s => (
                <span key={s} className="bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-medium text-slate-700">{s}</span>
              ))}
            </div>
            <p className="text-slate-500 text-sm mt-4">And all surrounding areas</p>
          </div>
        </section>

        <section className="py-12 bg-gradient-to-r from-[#E74C3C] to-[#c0392b] text-white text-center">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Ipswich's Local Pest Experts</h2>
            <p className="text-lg text-white/80 mb-6">Get your instant quote above or call us now for same-day service.</p>
            <a href="tel:0737081422" className="inline-flex items-center gap-2 bg-white text-[#E74C3C] font-bold px-8 py-3 rounded-full hover:bg-slate-100 transition-all">
              <Phone className="w-5 h-5" /> Call (07) 3708 1422
            </a>
          </div>
        </section>
      </div>
    </>
  );
}
